import { getContactsReport } from '@/app/services/reports/reports';
import * as Actions from '@/app/store/actions';
import { Avatar } from '@material-ui/core';
import Pusher from 'pusher-js';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import secureLocalStorage from 'react-secure-storage';
import { useReactToPrint } from 'react-to-print';
import AppliedFiltersBar from '@shared/Reports/AppliedFiltersBar';
import ReportsHeader from '@shared/Reports/ReportsHeader';
import TableTooltip from '@shared/Reports/TableTooltip';
import ContactsFiltersBar from './ContactsFiltersBar';
import ContactsPrintViewTable from './ContactsPrintTable';
import ContactsTable from './ContactsTable';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

const ContactsReports = () => {
	const [filters, setFilters] = useState({
		room_id: [],
		student_id: [],
		school_id: null,
		status: { label: 'Active', value: true },
		timezone: null,
		sort: 'student',
		dir: 'asc',
		page: 1,
		emergency_contact: null,
		student_name: 'last',
		contact_type: [],
		privileges: [],
	});
	const user = useSelector((state) => state.auth.user);
	const viewAsId = secureLocalStorage.getItem('view_as_id');
	const authToken = secureLocalStorage.getItem('jwt_access_token');
	const [loading, setLoading] = useState(false);
	const [printData, setPrintData] = useState([]);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [printDataLoading, setPrintDataLoading] = useState(false);
	const printRef = useRef();
	const [rows, setRows] = useState([]);
	const [totalResults, setTotalResults] = useState(0);
	const dispatch = useDispatch();
	const cache = useRef({}); // Cache object to prevent refetching pages already fetched
	const prevFilters = useRef(filters); // Track previous filters to know when to refetch a set of results
	const [exportType, setExportType] = useState(null);
	const [exportLoading, setExportLoading] = useState(false);
	const exportTimeoutRef = useRef(null);
	const navigate = useNavigate();

	useEffect(() => {
		const pusher = new Pusher(import.meta.env.VITE_PUSHER_CHANNEL_ID, {
			cluster: import.meta.env.VITE_PUSHER_CLUSTER_ID,
			channelAuthorization: {
				headers: {
					Authorization: `Bearer ${authToken}`,
				},
				endpoint: `${import.meta.env.VITE_API_ENDPOINT}broadcasting/auth`,
			},
		});
		const channel = pusher.subscribe(
			`private-App.Models.User.${user.role[0] === 'super_school_admin' ? viewAsId : user.data?.id}`
		);

		channel.bind('report.export.complete', (res) => {
			const exportLink = JSON.parse(window.localStorage.getItem('export_uuid'));
			if (exportLink && exportLink === res.uuid) {
				setExportLoading(false);
				if (exportTimeoutRef.current) {
					clearTimeout(exportTimeoutRef.current);
				}
				window.localStorage.removeItem('export_uuid');

				const anchor = document.createElement('a');
				anchor.href = res.link;
				anchor.download = res.filename;
				anchor.click();
				anchor.remove();
			}
		});

		return () => {
			pusher.disconnect();
		};
	}, []);

	const handlePrint = useReactToPrint({
		content: () => printRef.current,
	});

	useEffect(() => {
		setFilters({
			...filters,

			school_id: user.data.school.id,
		});
	}, [user]);

	const updateFilters = (newFilters) => {
		const filtersChanged = Object.keys(newFilters).some((key) => key !== 'page' && newFilters[key] !== filters[key]);
		if (filtersChanged) {
			setFilters({ ...newFilters, page: 1 });
		} else {
			setFilters(newFilters);
		}
	};

	const generateQueryParams = (filters, exportType = null) => {
		return {
			page: filters.page,
			start_date: filters.start_date,
			end_date: filters.end_date,
			status: filters.status?.value === true ? 1 : filters.status?.value === false ? 0 : null,
			timezone: filters.timezone,
			export: exportType,
			sort: filters.sort,
			dir: filters.dir,
			contact_type: filters.contact_type?.length > 0 ? filters.contact_type.map((item) => item.value) : null,
			privileges: filters.privileges?.length > 0 ? filters.privileges.map((item) => item.value) : null,
			student_id: filters.student_id?.length > 0 ? filters.student_id.map((item) => item.value) : null,
			room_id: filters.room_id?.length > 0 ? filters.room_id.map((item) => item.value) : null,
			student_name: filters.student_name,
		};
	};

	useEffect(() => {
		// Clear cache if any filter other than page changes
		const filtersChanged = Object.keys(filters).some(
			(key) => key !== 'page' && filters[key] !== prevFilters.current[key]
		);
		if (filtersChanged) {
			cache.current = {};
		}
		prevFilters.current = filters;

		if (filters.school_id) {
			setLoading(true);
			const queryParams = generateQueryParams(filters);
			const queryKey = JSON.stringify(queryParams);

			// Check if the results for the current page are already in the cache
			if (cache.current[queryKey]) {
				const cachedData = cache.current[queryKey];
				setRows(cachedData.data);
				setTotalResults(cachedData.total);
				setLoading(false);
			} else {
				setLoading(true);
				getContactsReport(queryParams)
					.then((res) => {
						setRows(res.data.data);
						setTotalResults(res.data.meta.total);
						// Store the results in the cache
						cache.current[queryKey] = {
							data: res.data.data,
							total: res.data.meta.total,
						};

						// Fetch stats data only when filters other than page change and total results > 0
					})
					.catch(() => {
						dispatch(
							Actions.showMessage({
								message: 'Failed to fetch report',
								variant: 'error',
							})
						);
						setRows([]);
						setTotalResults(0);
					})
					.finally(() => {
						setLoading(false);
					});
			}
		} else {
			// Clear data if start_date or end_date is not set
			setRows([]);
			setTotalResults(0);
		}
	}, [filters]);

	const handleExport = async (type) => {
		const queryParams = generateQueryParams(filters, type);
		try {
			setExportLoading(true);
			queryParams.uuid = uuidv4();
			window.localStorage.setItem('export_uuid', JSON.stringify(queryParams.uuid));
			await getContactsReport(queryParams);

			// Set a timeout to reset exportLoading if no response within 20 seconds
			exportTimeoutRef.current = setTimeout(() => {
				if (!exportLoading) return;
				setExportLoading(false);
				dispatch(
					Actions.showMessage({
						message: 'Export failed to generate. Please try again.',
						variant: 'error',
					})
				);
			}, 20000);
		} catch (error) {
			dispatch(
				Actions.showMessage({
					message: 'Failed to export report',
					variant: 'error',
				})
			);
		}
	};

	const handlePrintClick = async () => {
		setExportType('print');

		setPrintDataLoading(true);
		try {
			const queryParams = generateQueryParams(filters, 'print');

			const res = await getContactsReport(queryParams);
			setPrintData(res.data.data);
		} catch (error) {
			console.error('Error fetching all pages for print:', error);
		} finally {
			setPrintDataLoading(false);
		}
	};

	useEffect(() => {
		if (!printDataLoading && printData.length > 0) {
			handlePrint();
		}
	}, [printDataLoading, printData]);

	const processPhoneNumber = (phone) => {
		// Remove all non-digit characters
		let cleaned = phone?.replace(/\D/g, '');

		// Remove leading 1 if present
		if (cleaned?.startsWith('1')) {
			cleaned = cleaned?.substring(1);
		}

		// Format the number as (xxx) xxx-xxxx
		const formatted = cleaned?.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');

		return formatted;
	};

	const capitalizeFirstLetter = (str) => {
		return str
			?.split(' ')
			?.map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1))
			?.join(' ');
	};

	const getFormattedAddress = (row) => {
		return `${row?.address || ''}
${row?.address2 ? `${row.address2}\n` : ''}
${row?.city ? `${row.city}, ` : ''}${row?.state || ''}${row?.zip_code ? ` ${row.zip_code}` : ''}`;
	};

	const getFormattedAddressJSX = (row) => {
		return (
			<div className="whitespace-normal">
				{row?.address ? <div>{row.address}</div> : null}
				{row?.address2 ? <div>{row.address2}</div> : null}
				<div>
					{row?.city ? `${row.city}, ` : null}
					{row?.state ? row.state : null}
					{row?.zip_code ? ` ${row.zip_code}` : null}
				</div>
			</div>
		);
	};

	const columns = [
		{
			id: 'student',
			label: 'Student',
			render: (row, isStudent) => (
				<div className={`flex items-center gap-16 ${isStudent ? 'font-bold text-white' : ''}`}>
					<Avatar src={row.thumb} />
					{filters.student_name === 'last' && isStudent
						? capitalizeFirstLetter(`${row.last_name}, ${row.first_name}`)
						: capitalizeFirstLetter(`${row.first_name} ${row.last_name}`)}
				</div>
			),
			printRender: (row, isStudent) => {
				return isStudent ? (
					<div className={isStudent ? 'font-bold flex justify-between gap-16' : ''}>
						<div className="flex items-center gap-16">
							<span>
								{filters.student_name === 'last' && isStudent
									? capitalizeFirstLetter(`${row.last_name}, ${row.first_name}`)
									: capitalizeFirstLetter(`${row.first_name} ${row.last_name}`)}
							</span>
							<span>{row.room.name}</span>
						</div>
						<span style={{ maxWidth: '30ch', textAlign: 'right' }}>{getFormattedAddressJSX(row)}</span>
					</div>
				) : (
					<span>{capitalizeFirstLetter(row.first_name) + ' ' + capitalizeFirstLetter(row.last_name)}</span>
				);
			},
			widthClass: 'min-w-256',
		},
		{
			id: 'relation',
			label: 'Relation',
			render: (row, isStudent) =>
				isStudent ? (
					<div className="flex items-center gap-16 text-white font-bold">
						<Avatar src={row?.room?.thumb} />
						{row?.room.name}
					</div>
				) : (
					capitalizeFirstLetter(row.relation_with_child)
				),
			printRender: (row, isStudent) =>
				isStudent ? (
					<span className="font-bold text-10">{row.room.name}</span>
				) : (
					<span>{capitalizeFirstLetter(row?.relation_with_child)}</span>
				),
			widthClass: 'max-w-96',
		},
		{
			id: 'phone',
			widthClass: 'max-w-128',
			label: 'Phone Number',
			render: (row, isStudent) => (isStudent ? null : processPhoneNumber(row?.phone)),
			printRender: (row, isStudent) => (isStudent ? null : processPhoneNumber(row?.phone)),
		},
		{
			id: 'email',
			label: 'Email',
			render: (row, isStudent) =>
				isStudent ? null : (
					<TableTooltip title={row.email}>
						<div className="truncate">{row.email}</div>
					</TableTooltip>
				),
			printRender: (row, isStudent) => (isStudent ? null : <span className="pr-10">{row.email}</span>),
			widthClass: 'min-w-256',
		},
		{
			id: 'emergency_contact',
			label: 'Emergency Contact',
			render: (row, isStudent) => {
				return !isStudent && <span>{row.emergency_contact ? 'Yes' : ''}</span>;
			},
			printRender: (row, isStudent) => !isStudent && <span>{row.emergency_contact ? 'Yes' : ''}</span>,
		},
		{
			id: 'address',
			label: 'Address',
			widthClass: 'min-w-256',
			render: (row, isStudent) => (
				<TableTooltip arrow interactive title={getFormattedAddressJSX(row)}>
					<div className={isStudent ? 'text-white truncate font-bold ' : 'truncate'}>{getFormattedAddress(row)}</div>
				</TableTooltip>
			),
			printRender: (row) => <span className="">{getFormattedAddressJSX(row)}</span>,
		},
	];
	return (
		<div>
			<ReportsHeader
				exportDisabled={!rows?.length}
				exportLoading={exportLoading}
				onBack={() => navigate(-1)}
				onDownloadPDF={() => handleExport('pdf')}
				onExport={handleExport}
				onPrint={handlePrintClick}
				pdfLoading={false}
				printLoading={printDataLoading}
				subtitle="A list of all students and their associated contacts."
				title="Contacts Report"
			/>
			<ContactsFiltersBar filters={filters} setFilters={updateFilters} />
			<AppliedFiltersBar filters={filters} setFilters={updateFilters} />
			<div className="bg-white">
				<div className="shadow-lg">
					<ContactsTable
						columns={columns}
						data={rows}
						dir={filters.dir}
						filters={filters}
						loading={loading}
						page={filters.page}
						rowsPerPage={rowsPerPage}
						setDir={(dir) => updateFilters({ ...filters, dir })}
						setFilters={updateFilters}
						setPage={(page) => updateFilters({ ...filters, page })}
						setRowsPerPage={setRowsPerPage}
						setSort={(sort) => updateFilters({ ...filters, sort })}
						sort={filters.sort}
						totalResults={totalResults}
					/>
				</div>
				<div className="h-0 overflow-hidden">
					<ContactsPrintViewTable
						columns={columns}
						data={printData}
						exportType={exportType}
						filters={filters}
						ref={printRef}
						title="Contacts Report"
						user={user}
					/>
				</div>
			</div>
		</div>
	);
};

export default ContactsReports;
